import React, { useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { FixedAssetLabelService } from '../../services/fixedAssetCount.service';
import { LABEL_LAYOUT_OPTIONS, LabelLayout } from '../../interfaces/fixedAsset/fixedAssetCount.interface';

interface Props {
  open: boolean;
  assetIds: string[];
  onClose: () => void;
}

/** Picks the sticker sheet and where to start on it (a half-used sheet keeps its spare stickers), then downloads the PDF. */
const AssetLabelDialog: React.FC<Props> = ({ open, assetIds, onClose }) => {
  const toast = useToast();
  const [layout, setLayout] = useState<LabelLayout>('A4_3x7');
  const [startPosition, setStartPosition] = useState('1');
  const [busy, setBusy] = useState(false);

  const option = LABEL_LAYOUT_OPTIONS.find(o => o.value === layout)!;
  const start = Number(startPosition);
  const startOk = Number.isInteger(start) && start >= 1 && start <= option.perPage;

  const print = async () => {
    setBusy(true);
    try {
      await FixedAssetLabelService.print({
        assetIds, layout, startPosition: option.perPage > 1 ? start : undefined,
      });
      toast.showSuccess('Labels downloaded');
      onClose();
    } catch (e) {
      toast.showApiError(e, 'Failed to print labels');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog data-help-page="fixedAssets.labelDialog.page" open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Print QR labels</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {assetIds.length} asset{assetIds.length === 1 ? '' : 's'} selected. Each label carries a QR code, the asset code and its name.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField data-help="fixedAssets.labelDialog.field.layout" select label="Sticker sheet" size="small" value={layout}
            onChange={e => { setLayout(e.target.value as LabelLayout); setStartPosition('1'); }}>
            {LABEL_LAYOUT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          {option.perPage > 1 && (
            <TextField data-help="fixedAssets.labelDialog.field.start" label="Start at label number" size="small" type="number" value={startPosition}
              onChange={e => setStartPosition(e.target.value)}
              error={!startOk} helperText={startOk ? `1 to ${option.perPage}. Skips stickers already used.` : `Enter 1 to ${option.perPage}`}
              inputProps={{ min: 1, max: option.perPage }} />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button data-help="fixedAssets.labelDialog.cancel" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button data-help="fixedAssets.labelDialog.save" variant="contained" onClick={print} disabled={busy || assetIds.length === 0 || !startOk}>
          {busy ? 'Preparing...' : 'Download PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetLabelDialog;
